import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { themes } from '../config/theme/themes';
import { fetchFirstUsableImage } from '../utils/imageSearch';
import { THEME_SOUNDS } from '../utils/themeSounds';
import { useProfileStore } from './profileStore';
import { getTextColor } from '../utils/colorUtils';
import { Theme } from './types/theme';

type ThemeState = {
    selectedTheme: Theme;
    themes: Theme[];
    compColor: 'white' | 'black';
    setTheme: (id: string) => void;
    addTheme: (theme: Theme) => void;
    updateTheme: (id: string, updatedProps: Partial<Theme>) => void;
    removeTheme: (id: string) => void;
    rotateBgImage: () => void;
};

// Track in-flight image fetches so we don't double-fetch.
const fetchingIds = new Set<string>();

/** Auto-fetch a background image for anime themes that don't have one yet. */
const maybeFetchBgImage = (theme: Theme) => {
    if (!theme.id.startsWith('anime-')) return;
    if (theme.bgImage) return;
    if (fetchingIds.has(theme.id)) return;
    if (!theme.bgQuery) return;

    fetchingIds.add(theme.id);
    fetchFirstUsableImage(theme.bgQuery)
        .then((url) => {
            if (url) {
                useThemeStore.getState().updateTheme(theme.id, { bgImage: url });
            }
        })
        .catch((err) => console.warn('Auto-fetch bg image failed:', err))
        .finally(() => {
            fetchingIds.delete(theme.id);
        });
};

/**
 * When switching to a theme that has themed sounds, auto-assign those sounds
 * to the active profile's sub-alarms (only if the sub-alarm still has the
 * default kid: sound — we don't override user-customised sounds).
 */
const maybeAutoAssignThemeSounds = (themeId: string) => {
    const themeSounds = THEME_SOUNDS[themeId];
    if (!themeSounds || themeSounds.length === 0) return;

    const profileState = useProfileStore.getState();
    const activeProfile = profileState.profiles.find((p) => p.id === profileState.activeProfileId);
    if (!activeProfile || activeProfile.subAlarms.length === 0) return;

    // Assign themed sounds to sub-alarms in order. Only override sub-alarms
    // that currently use a procedural kid: sound (i.e. not user-customised).
    let soundIdx = 0;
    activeProfile.subAlarms.forEach((sub) => {
        if (!sub.soundSrc.startsWith('kid:')) return; // respect user customisations
        const sound = themeSounds[soundIdx % themeSounds.length];
        soundIdx++;
        if (sound) {
            profileState.updateSubAlarm(activeProfile.id, sub.id, { soundSrc: sound.url });
        }
    });
};

export const useThemeStore = create<ThemeState>()(
    persist(
        (set, get) => ({
            selectedTheme: themes[0],
            themes: [...themes],
            compColor: getTextColor(themes[0].color.main),

            setTheme: (id) => {
                const { themes } = get();
                const theme = themes.find((t) => t.id === id);
                if (theme) {
                    set({
                        selectedTheme: theme,
                        compColor: getTextColor(theme.color.main),
                    });
                    maybeFetchBgImage(theme);
                    maybeAutoAssignThemeSounds(id);
                } else {
                    console.warn(`Theme with id "${id}" does not exist.`);
                }
            },

            addTheme: (theme) => {
                set((state) => ({
                    themes: [...state.themes, theme],
                }));
            },

            updateTheme: (id, updatedProps) =>
                set((state) => {
                    const updatedThemes = state.themes.map((t) => (t.id === id ? { ...t, ...updatedProps } : t));

                    return {
                        themes: updatedThemes,
                        ...(state.selectedTheme.id === id && {
                            selectedTheme: { ...state.selectedTheme, ...updatedProps },
                            compColor: updatedProps.color?.main
                                ? getTextColor(updatedProps.color.main)
                                : state.compColor,
                        }),
                    };
                }),

            removeTheme: (id) =>
                set((state) => {
                    if (id.startsWith('default-')) {
                        console.warn(`Cannot remove default theme: "${id}".`);
                        return {};
                    }
                    const updatedThemes = state.themes.filter((t) => t.id !== id);
                    const newSelectedTheme = state.selectedTheme.id === id ? updatedThemes[0] : state.selectedTheme;

                    return {
                        themes: updatedThemes,
                        selectedTheme: newSelectedTheme,
                        ...(state.selectedTheme.id === id && {
                            compColor: getTextColor(newSelectedTheme.color.main),
                        }),
                    };
                }),

            rotateBgImage: () =>
                set((state) => {
                    const theme = state.selectedTheme;
                    if (!theme.bgImages || theme.bgImages.length <= 1) return {};
                    const nextIndex = ((theme.bgImageIndex ?? 0) + 1) % theme.bgImages.length;
                    const newBgImage = theme.bgImages[nextIndex];
                    const updatedThemes = state.themes.map((t) =>
                        t.id === theme.id ? { ...t, bgImage: newBgImage, bgImageIndex: nextIndex } : t
                    );
                    return {
                        themes: updatedThemes,
                        selectedTheme: { ...theme, bgImage: newBgImage, bgImageIndex: nextIndex },
                    };
                }),
        }),
        {
            name: 'theme-store',
            version: 6, // a migration will be triggered if the version in the storage mismatches this one
            migrate: (persistedState, version) => {
                const state = persistedState as ThemeState;

                // v4→v6: refresh the themes list so newly-added/modified built-in themes
                // (e.g. Dragonball Z → Super Saiyan Blue recolor) appear even for users
                // who previously persisted the old list. Preserve user-created custom themes.
                if (version < 6) {
                    const persistedThemes = state.themes || [];
                    const customThemes = persistedThemes.filter(
                        (t) => !t.id.startsWith('default-') && !t.id.startsWith('anime-')
                    );
                    const mergedThemes = [...themes, ...customThemes];

                    // Preserve selected theme if it still exists; otherwise fall back to default.
                    const stillExists = persistedThemes.some((t) => t.id === state.selectedTheme?.id);
                    const newSelected = stillExists ? state.selectedTheme : themes[0];

                    return {
                        ...state,
                        themes: mergedThemes,
                        selectedTheme: newSelected,
                        compColor: getTextColor(newSelected.color.main),
                    };
                }
                return state;
            },
        }
    )
);
